import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import Papa from "papaparse";
import convertToLiters from "./convertToLiters";
import moment from "moment";
import isEmpty from "./is-Empty";

export const exportEnquiriesToCSV = (enquiries) => {
  const flatData = enquiries.map((enquiry) => {
    return {
      EnquiryID: enquiry.enquiry_id,
      EnquiryDate: moment(enquiry.enquiryCreatedAt)
        .tz("Asia/Kolkata")
        .format("DD/MM/YYYY hh:mm:ss A"),
      Buyer: enquiry.buyer_business_name,
      AcceptedDate: enquiry.accepted_at
        ? moment(enquiry.accepted_at).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
        : "",
      Product: enquiry.brand_name + " - " + enquiry.product_name,
      Variant: convertToLiters(JSON.parse(enquiry.other_details).variant),
      MOQ: enquiry.moqData?.displayName || enquiry.moq,
      TotalAmount: enquiry.total_product_amount,
    };
  });

  // Convert to CSV
  const csv = Papa.unparse(flatData);

  // Save as file
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `report_enquiries_${new Date().toISOString().split("T")[0]}.csv`);
};

export const exportTotalTripsToCSV = (trips) => {
  const flatData = trips.map((trip) => {
    return {
      OrderId: trip.orderId,
      OrderDate: moment(trip.order_date).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A"),
      SellerName: trip.sellerInfo.business_name,
      TripStatus: trip.trip_status,
      VehicleNumber: trip.vehicleNumber,
      Brand: trip.productVariant.brand_name,
      Variant: convertToLiters(trip.productVariant.quantity),
      MOQ: trip.moq,
      AssignedDate: !isEmpty(trip.shipped_at)
        ? moment(trip.shipped_at).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
        : "",
      AcceptedTripDate: !isEmpty(trip.lp_accepted_at)
        ? moment(trip.lp_accepted_at).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
        : "",
      OutForDeliveryDate: !isEmpty(trip.outfordelivery_at)
        ? moment(trip.outfordelivery_at).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
        : "",
      DeliveredDate: !isEmpty(trip.delivered_at)
        ? moment(trip.delivered_at).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
        : "",
    };
  });

  // Convert to CSV
  const csv = Papa.unparse(flatData);

  // Save as file
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `total_trips_${new Date().toISOString().split("T")[0]}.csv`);
};

export const exportPayoutOrdersToExcel = (orders) => {
  const data = orders.map((order) => [
    order.invoiceDate,
    order.invoiceNumber,
    order.basicAmount,
    order.gstAmount,
    order.totalInvoiceAmount,
    order.mop,
    order.deductionTCS,
    order.bhareeCommPercent + "%",
    order.deductionCharges,
    order.deductionChargesGST,
    order.totalDeductionAmount,
    order.bhareePayableAmount,
    order.payout ? "Done" : "Not Done",
    order.payoutDate
      ? moment(order.payoutDate).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
      : "",
  ]);

  const wsData = [
    ["INPUT", "", "", "", "", "", "BHAREE DEDUCTIONS", "", "", "", "", "CREDITS", "", ""],
    [
      "Invoice Date",
      "Invoice Number",
      "Basic Amount",
      "GST Amount",
      "Total Invoice Amount",
      "MOP",
      "TCS",
      "Charges %",
      "Charges",
      "GST on Charges",
      "Total Deduction Amount",
      "Bharee Payable Amount",
      "Payout",
      "Payout Date",
    ],
    ...data,
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // INPUT
    { s: { r: 0, c: 6 }, e: { r: 0, c: 10 } }, // Bharee Deductions
    { s: { r: 0, c: 11 }, e: { r: 0, c: 13 } }, // CREDITS
  ];

  const headerStyle = {
    font: { bold: true },
    alignment: { horizontal: "center", vertical: "center" },
  };

  const headerCells = [
    "A1",
    "G1",
    "L1",
    "A2",
    "B2",
    "C2",
    "D2",
    "E2",
    "F2",
    "G2",
    "H2",
    "I2",
    "J2",
    "K2",
    "L2",
    "M2",
    "N2",
  ];
  headerCells.forEach((cell) => {
    ws[cell].s = headerStyle;
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Payouts");

  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `payouts_${new Date().toISOString().split("T")[0]}.xlsx`);
};

export const exportPayoutOrdersToCSV = (orders) => {
  // Flatten each order for CSV
  const flatData = orders.map((order) => {
    return {
      InvoiceDate: order.invoiceDate,
      InvoiceNumber: order.invoiceNumber,
      BasicAmount: order.basicAmount,
      GSTAmount: order.gstAmount,
      TotalInvoiceAmount: order.totalInvoiceAmount,
      MOP: order.mop,
      "Bharee Deduction TCS": order.deductionTCS,
      "Bharee Deduction Charges": order.deductionCharges + " (" + order.bhareeCommPercent + "%)",
      "Bharee Deduction GST on Charges": order.deductionChargesGST,
      TotalDeductionAmount: order.totalDeductionAmount,
      BhareePayableAmount: order.bhareePayableAmount,
      Payout: order.payout ? "Done" : "Not Done",
      PayoutDate: order.payoutDate
        ? moment(order.payoutDate).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm:ss A")
        : "",
    };
  });

  // Convert to CSV
  const csv = Papa.unparse(flatData);

  // Save as file
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `payouts_${new Date().toISOString().split("T")[0]}.csv`);
};

export const exportOrdersToCSV = (orders) => {
  // Flatten each order for CSV
  const flatData = orders.map((order) => {
    const product = order.product_variant_id?.[0] || {};
    const deliveryMethod = order.delivery_method_id?.[0] || {};
    const billingAddress = order.billing_address_id || {};
    const seller = order.seller_id || {};
    const buyer = order.buyerInfo || {};

    return {
      Order_ID: order.orderId,
      Invoice_ID: order.invoice_id,
      Invoice_Date: order.invoice_date,
      Order_Date: order.order_date,
      Expected_Delivery_Date: order.expected_delivery_date,
      Status: order.status,
      Payment_Status: order.payment_status_id,

      // Seller Info
      Seller_Name: seller.business_name,
      Seller_ID: seller.seller_id,
      Seller_City: order.seller_address_id?.city || "",
      Seller_State: order.seller_address_id?.state || "",

      // Buyer Info
      Buyer_Name: buyer.business_name,
      Buyer_GST: buyer.gst_no,
      Buyer_City: billingAddress.city,
      Buyer_State: billingAddress.state,

      // Product Info
      Product_Name: product.product_name,
      Brand: product.brand_name,
      MOQ: order.moq,
      Unit_Price: product.price,
      Quantity: convertToLiters(product.quantity),
      Delivery_Method: deliveryMethod.name,
      Delivery_Subtitle: deliveryMethod.subTitle,

      // Amounts
      Total_Amount: order.totalAmount,
      Delivery_Charge: order.totalDelivery,
      Handling_Charge: order.totalHandlingCharges,
      Payable_Amount: order.payableAmount,
    };
  });

  // Convert to CSV
  const csv = Papa.unparse(flatData);

  // Save as file
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `orders_${new Date().toISOString().split("T")[0]}.csv`);
};
